import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import type { TreeProps, TreeRef } from './Tree.types';
import { TreeNode } from './parts/TreeNode';
import { useTree } from './hooks/useTree';
import type { NodeState } from './utils/treeOperations';

/**
 * Tree — Orchestrator.
 *
 * Wires together useTree (state), TreeNode (rows), and TreeCheckbox (appearance).
 * Manages keyboard navigation with roving tabindex and exposes TreeRef via forwardRef.
 *
 * This component MUST NOT contain business logic — everything is delegated to
 * useTree or pure functions in treeOperations.ts.
 */
const TreeInner = <T = unknown>(
  props: TreeProps<T>,
  ref: React.ForwardedRef<TreeRef<T>>
): React.ReactElement => {
  const {
    nodes: inputNodes,
    readOnly = false,
    onChange,
    beforeCheck,
    defaultExpanded = false,
    expandedIds: expandedIdsProp,
    showBadge = false,
    showDividers = false,
    ariaLabel,
    className,
  } = props;

  const tree = useTree(inputNodes, {
    beforeCheck,
    onChange,
    defaultExpanded,
    expandedIds: expandedIdsProp,
    readOnly,
  });

  // Expose imperative API via ref
  useImperativeHandle(
    ref,
    () => ({
      getAll: tree.getAll,
      getChecked: tree.getChecked,
      getDisabled: tree.getDisabled,
      getModified: tree.getModified,
      validate: tree.validate,
    }),
    [tree.getAll, tree.getChecked, tree.getDisabled, tree.getModified, tree.validate]
  );

  // DOM refs for roving tabindex focus management
  const nodeRefs = useRef<Map<string, HTMLElement>>(new Map());

  // Programmatically move DOM focus when focusedId changes
  useEffect(() => {
    if (tree.focusedId) {
      nodeRefs.current.get(tree.focusedId)?.focus();
    }
  }, [tree.focusedId]);

  // Visible nodes as NodeState[] for rendering
  const visibleNodes = tree.nodes;

  /**
   * Handle expand/collapse toggle.
   * If already expanded, collapse. Otherwise expand.
   */
  const handleExpandToggle = useCallback(
    (nodeId: string) => {
      if (tree.expandedIds.has(nodeId)) {
        tree.collapse(nodeId);
      } else {
        tree.expand(nodeId);
      }
    },
    [tree.expandedIds, tree.collapse, tree.expand]
  );

  /**
   * Keyboard navigation handler.
   * Attached to the role="tree" container, dispatched per-node via onKeyDown prop.
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, nodeId: string) => {
      const currentIndex = visibleNodes.findIndex((n) => n.id === nodeId);
      if (currentIndex === -1) return;

      const currentNode = visibleNodes[currentIndex];

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          if (currentIndex < visibleNodes.length - 1) {
            tree.setFocusedId(visibleNodes[currentIndex + 1].id);
          }
          break;
        }

        case 'ArrowUp': {
          e.preventDefault();
          if (currentIndex > 0) {
            tree.setFocusedId(visibleNodes[currentIndex - 1].id);
          }
          break;
        }

        case 'ArrowRight': {
          e.preventDefault();
          const hasChildren = currentNode.childIds.length > 0;
          if (hasChildren && !tree.expandedIds.has(nodeId)) {
            // Expand collapsed branch
            tree.expand(nodeId);
          } else if (hasChildren && tree.expandedIds.has(nodeId)) {
            // Move to first child if expanded
            const firstChildIndex = visibleNodes.findIndex(
              (n) => n.id === currentNode.childIds[0]
            );
            if (firstChildIndex !== -1) {
              tree.setFocusedId(visibleNodes[firstChildIndex].id);
            }
          }
          break;
        }

        case 'ArrowLeft': {
          e.preventDefault();
          const hasBranchChildren = currentNode.childIds.length > 0;
          if (hasBranchChildren && tree.expandedIds.has(nodeId)) {
            // Collapse expanded branch
            tree.collapse(nodeId);
          } else if (currentNode.parentId !== null) {
            // Move to parent
            tree.setFocusedId(currentNode.parentId);
          }
          break;
        }

        case ' ': {
          e.preventDefault();
          if (!currentNode.disabled && !readOnly) {
            tree.toggle(nodeId);
          }
          break;
        }

        case 'Home': {
          e.preventDefault();
          if (visibleNodes.length > 0) {
            tree.setFocusedId(visibleNodes[0].id);
          }
          break;
        }

        case 'End': {
          e.preventDefault();
          if (visibleNodes.length > 0) {
            tree.setFocusedId(visibleNodes[visibleNodes.length - 1].id);
          }
          break;
        }

        default:
          break;
      }
    },
    [visibleNodes, tree.expandedIds, tree.expand, tree.collapse, tree.setFocusedId, tree.toggle, readOnly]
  );

  /**
   * Ref callback to collect DOM elements for roving tabindex focus.
   * Stores the role="treeitem" child element, not the wrapper.
   */
  const setNodeRef = useCallback((nodeId: string, el: HTMLElement | null) => {
    if (el) {
      const treeItem = el.querySelector('[role="treeitem"]') as HTMLElement | null;
      nodeRefs.current.set(nodeId, treeItem ?? el);
    } else {
      nodeRefs.current.delete(nodeId);
    }
  }, []);

  /**
   * Handle focus on a node — update roving tabindex state.
   * Called when user clicks or tabs into a treeitem.
   */
  const handleNodeFocus = useCallback(
    (nodeId: string) => {
      tree.setFocusedId(nodeId);
    },
    [tree.setFocusedId]
  );

  // Track which root nodes appear to render dividers between them
  let rootNodeIndex = 0;
  const totalRootNodes = visibleNodes.filter((n) => n.parentId === null).length;

  return (
    <div
      role="tree"
      aria-label={ariaLabel}
      aria-readonly={readOnly || undefined}
      className={className}
    >
      {visibleNodes.map((node) => {
        const isRoot = node.parentId === null;
        const showDivider = showDividers && isRoot && rootNodeIndex > 0;

        if (isRoot) {
          rootNodeIndex++;
        }

        return (
          <React.Fragment key={node.id}>
            {showDivider && (
              <hr className="border-border-primary dark:border-dark-border-primary my-1" />
            )}
            <TreeNodeWithRef
              node={node}
              level={node.depth}
              expanded={tree.expandedIds.has(node.id)}
              hasChildren={node.childIds.length > 0}
              readOnly={readOnly}
              showBadge={showBadge}
              childCount={node.childIds.length}
              onToggle={tree.toggle}
              onExpand={handleExpandToggle}
              tabIndex={node.id === tree.focusedId ? 0 : -1}
              onKeyDown={handleKeyDown}
              setRef={setNodeRef}
              onNodeFocus={handleNodeFocus}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
};

/**
 * Wrapper to attach the ref callback to each TreeNode's DOM element.
 * This allows roving tabindex focus management without modifying TreeNode.
 */
interface TreeNodeWithRefProps<T = unknown> {
  node: NodeState<T>;
  level: number;
  expanded: boolean;
  hasChildren: boolean;
  readOnly: boolean;
  showBadge: boolean;
  childCount: number;
  onToggle: (nodeId: string) => void;
  onExpand: (nodeId: string) => void;
  tabIndex: number;
  onKeyDown: (e: React.KeyboardEvent, nodeId: string) => void;
  setRef: (nodeId: string, el: HTMLElement | null) => void;
  onNodeFocus: (nodeId: string) => void;
}

function TreeNodeWithRef<T = unknown>(props: TreeNodeWithRefProps<T>): React.ReactElement {
  const { setRef, onNodeFocus, node, ...treeNodeProps } = props;

  const refCallback = useCallback(
    (el: HTMLElement | null) => {
      setRef(node.id, el);
    },
    [setRef, node.id]
  );

  const handleFocus = useCallback(() => {
    onNodeFocus(node.id);
  }, [onNodeFocus, node.id]);

  return (
    <div ref={refCallback} onFocus={handleFocus}>
      <TreeNode node={node} {...treeNodeProps} />
    </div>
  );
}

export const Tree = forwardRef(TreeInner) as <T = unknown>(
  props: TreeProps<T> & { ref?: React.Ref<TreeRef<T>> }
) => React.ReactElement;

(Tree as unknown as { displayName: string }).displayName = 'Tree';
